/**
 * State Machines Panel
 * 
 * Integrated XState visualization panel for the debug HUD.
 * Shows all actors with live state highlighting.
 */

import { LitElement, html, css } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { sharedStyles } from '../styles/shared-styles';
import { StateManager, type EventLogEntry } from '../../state/StateManager';
// TODO: Fix xstatexray package resolution
// import 'xstatexray';
import type { ActorInfo } from '../components/xstate/xstate-actor-list';
import { EntityType } from '../../types/EntityTypes';
import '../components/xstate';

import { revealPanel } from '../uiActions';

const STORAGE_KEY = 'himum.stateMachinesPanel.pinnedActors';

// Force update
@customElement('state-machines-panel')
export class StateMachinesPanel extends LitElement {
  static styles = [
    sharedStyles,
    css`
    :host {
      display: block;
      height: 100%;
      color: #eee;
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
      font-size: 12px;
    }

    .panel-content {
      display: flex;
      flex-direction: column;
      height: 100%;
      overflow: hidden;
    }

    .header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 8px 12px;
      border-bottom: 1px solid rgba(255, 255, 255, 0.1);
      background: rgba(0, 0, 0, 0.2);
    }

    .header-title {
      font-weight: 600;
      font-size: 13px;
      display: flex;
      align-items: center;
      gap: 6px;
    }

    .status-indicator {
      width: 8px;
      height: 8px;
      border-radius: 50%;
      background: #666;
    }

    .status-indicator.live {
      background: #6f6;
      animation: pulse 1.5s ease-in-out infinite;
    }

    @keyframes pulse {
      0%, 100% { opacity: 1; }
      50% { opacity: 0.5; }
    }

    .content-area {
      flex: 1;
      overflow-y: auto;
      padding: 8px;
      display: flex;
      flex-direction: column;
      gap: 12px;
    }

    .actor-list-section {
      flex-shrink: 0;
    }

    .visualizer-section {
      flex: 1;
      min-height: 200px;
    }

    .section-title {
      font-size: 10px;
      text-transform: uppercase;
      color: #888;
      margin-bottom: 6px;
      padding-bottom: 4px;
      border-bottom: 1px solid rgba(255, 255, 255, 0.1);
    }

    .empty-state {
      text-align: center;
      padding: 20px;
      color: #888;
      font-style: italic;
    }

    .filters {
      display: flex;
      gap: 8px;
      padding: 8px;
      background: rgba(0, 0, 0, 0.2);
      border-bottom: 1px solid rgba(255, 255, 255, 0.1);
      flex-wrap: wrap;
      align-items: center;
    }
    
    .filter-group {
      display: flex;
      gap: 4px;
      align-items: center;
    }
    
    .filter-label {
      font-size: 10px;
      color: #888;
      text-transform: uppercase;
    }

    .event-log-section {
      flex-shrink: 0;
      max-height: 180px;
      overflow-y: auto;
      border-top: 1px solid rgba(255, 255, 255, 0.1);
      padding-top: 8px;
    }

    .event-entry {
      display: flex;
      align-items: flex-start;
      padding: 3px 6px;
      margin-bottom: 2px;
      border-radius: 3px;
      font-size: 10px;
      font-family: 'Consolas', 'Monaco', monospace;
      background: rgba(255, 255, 255, 0.03);
    }

    .event-entry.transitioned {
      background: rgba(100, 255, 100, 0.1);
      border-left: 2px solid #6f6;
    }

    .event-entry.no-transition {
      background: rgba(255, 255, 100, 0.05);
      border-left: 2px solid #664;
    }

    .event-time {
      color: #666;
      margin-right: 8px;
      flex-shrink: 0;
      width: 50px;
    }

    .event-type {
      color: #8cf;
      font-weight: 600;
      margin-right: 8px;
      min-width: 100px;
    }

    .event-states {
      color: #aaa;
      flex: 1;
    }

    .event-arrow {
      color: #888;
      margin: 0 4px;
    }

    .state-before {
      color: #f88;
    }

    .state-after {
      color: #8f8;
    }

    .state-same {
      color: #888;
    }
  `];

  @state() private actors: ActorInfo[] = [];
  @state() private selectedActorId: string | null = null;
  @state() private pinnedActorIds: string[] = [];
  @state() private isSubscribed: boolean = false;
  @state() private eventLog: EventLogEntry[] = [];
  @state() private showEventLog: boolean = true;
  
  // Filter states
  @state() private filterName: string = '';
  @state() private filterType: 'all' | 'character' | 'projectile' | 'object' = 'all';
  @state() private filterOnScreen: 'all' | 'onscreen' | 'offscreen' = 'all';

  private unsubscribe: (() => void) | null = null;
  private updateInterval: number | null = null;

  private onSelectActorBound = (e: Event) => {
    const ce = e as CustomEvent<{ actorId: string }>;
    if (ce.detail && ce.detail.actorId) {
      this.selectedActorId = ce.detail.actorId;
      revealPanel('states');
    }
  };

  connectedCallback(): void {
    super.connectedCallback();
    window.addEventListener('himum:select-actor', this.onSelectActorBound);
    this.loadPinnedActors();
    // this.topology = extractCharacterMachineTopology(); // Removed as we use xstatexray
    this.startUpdates();
  }

  disconnectedCallback(): void {
    window.removeEventListener('himum:select-actor', this.onSelectActorBound);
    this.stopUpdates();
    super.disconnectedCallback();
  }

  private loadPinnedActors() {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        this.pinnedActorIds = JSON.parse(stored);
      }
    } catch {
      // Ignore
    }
  }

  private savePinnedActors() {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(this.pinnedActorIds));
    } catch {
      // Ignore
    }
  }

  private startUpdates() {
    const stateManager = StateManager.getInstance();
    
    // Subscribe to debug updates
    this.unsubscribe = stateManager.subscribeDebug((snapshot) => {
      // Update actors list with extended info
      const actorsList: ActorInfo[] = [];
      
      // Add characters with extended info
      for (const char of snapshot.characters) {
        actorsList.push({
          id: char.characterId,
          type: 'character' as const,
          entityType: EntityType.CHARACTER,
          behaviorId: char.characterDefId,
          stateLabel: char.locomotionState,
          position: char.position ? { x: char.position.x, y: char.position.y, z: char.position.z } : undefined,
          health: { current: char.health, max: 100 }, // TODO: get actual max health from context
          velocity: char.velocity ? { x: char.velocity.x, y: char.velocity.y, z: char.velocity.z } : undefined,
          isOnScreen: undefined, // TODO: add to CharacterDebugSnapshot
          recentStates: [], // TODO: add state history to CharacterDebugSnapshot
          // Character-specific fields
          locomotionState: char.locomotionState,
          combatState: char.combatState,
          healthState: char.healthState,
          facingDirection: char.facingDirection,
          isGrounded: char.isGrounded,
          attackPhase: char.attackPhase,
          attackId: char.attackId,
        });
      }

      // Add projectiles
      for (const proj of snapshot.projectiles) {
        actorsList.push({
          id: proj.projectileId,
          type: 'projectile',
          entityType: EntityType.PROJECTILE,
          behaviorId: proj.specId,
          stateLabel: proj.flightState,
          position: proj.position ? { x: proj.position.x, y: proj.position.y, z: proj.position.z } : undefined,
          velocity: proj.velocity ? { x: proj.velocity.x, y: proj.velocity.y, z: proj.velocity.z } : undefined,
          isOnScreen: undefined,
          recentStates: []
        });
      }

      // Add dynamic objects
      // TODO: Ensure behaviorId is correctly populated from context
      for (const obj of snapshot.dynamicObjects) {
        actorsList.push({
          id: obj.objectId,
          assetId: obj.context?.assetId,
          type: 'object' as any,
          entityType: EntityType.DYNAMIC_OBJECT,
          behaviorId: obj.context?.behaviorId || obj.context?.assetId,
          stateLabel: obj.state,
          health: obj.health !== undefined ? { current: obj.health, max: obj.context.maxHealth || 100 } : undefined,
          isOnScreen: undefined,
          recentStates: []
        });
      }
      
      this.actors = actorsList;

      // Auto-select first actor if none selected
      if (!this.selectedActorId && this.actors.length > 0) {
        this.selectedActorId = this.actors[0].id;
      }

      // Update current state for selected actor
      if (this.selectedActorId) {
        // Filter event log for selected actor
        this.eventLog = snapshot.eventLog
          .filter(e => e.characterId === this.selectedActorId)
          .slice(-15); // Last 15 events
      }

      this.isSubscribed = true;
    });
  }

  private stopUpdates() {
    if (this.unsubscribe) {
      this.unsubscribe();
      this.unsubscribe = null;
    }
    if (this.updateInterval !== null) {
      clearInterval(this.updateInterval);
      this.updateInterval = null;
    }
    this.isSubscribed = false;
  }

  private handleActorSelected(e: CustomEvent<{ actorId: string }>) {
    this.selectedActorId = e.detail.actorId;
  }

  private handleActorPinned(e: CustomEvent<{ actorId: string; pinned: boolean }>) {
    if (e.detail.pinned) {
      this.pinnedActorIds = [...this.pinnedActorIds, e.detail.actorId];
    } else {
      this.pinnedActorIds = this.pinnedActorIds.filter(id => id !== e.detail.actorId);
    }
    this.savePinnedActors();
  }

  private getSelectedActor() {
    if (!this.selectedActorId) return null;
    
    const stateManager = StateManager.getInstance();
    
    // Try to find the actor in all registries
    const characterActor = stateManager.getCharacterActor(this.selectedActorId);
    if (characterActor) return characterActor;
    
    const projectileActor = stateManager.getProjectileActor(this.selectedActorId);
    if (projectileActor) return projectileActor;
    
    const dynamicObjectActor = stateManager.getDynamicObjectActor(this.selectedActorId);
    if (dynamicObjectActor) return dynamicObjectActor;
    
    return null;
  }
  
  private getFilteredActors(): ActorInfo[] {
    return this.actors.filter(actor => {
      // Name filter
      if (this.filterName && !actor.id.toLowerCase().includes(this.filterName.toLowerCase())) {
        return false;
      }
      
      // Type filter
      if (this.filterType !== 'all' && actor.type !== this.filterType) {
        return false;
      }
      
      // On screen filter
      if (this.filterOnScreen !== 'all') {
        if (actor.isOnScreen === undefined) return true; // Include if we don't know
        if (this.filterOnScreen === 'onscreen' && !actor.isOnScreen) return false;
        if (this.filterOnScreen === 'offscreen' && actor.isOnScreen) return false;
      }
      
      return true;
    });
  }

  render() {
    const filteredActors = this.getFilteredActors();
    
    return html`
      <div class="panel-content">
        <div class="header">
          <span class="header-title">
            <div class="status-indicator ${this.isSubscribed ? 'live' : ''}"></div>
            State Machines
          </span>
          <div>
            <button 
              class="toggle-btn ${this.showEventLog ? 'active' : ''}" 
              @click=${() => this.showEventLog = !this.showEventLog}
              title="Toggle event log"
            >📋 Events</button>
            <button class="refresh-btn" @click=${() => this.requestUpdate()}>↻</button>
          </div>
        </div>
        
        <div class="filters">
          <div class="filter-group">
            <span class="filter-label">Name:</span>
            <input 
              type="text" 
              class="filter-input" 
              placeholder="Search..."
              .value=${this.filterName}
              @input=${(e: Event) => this.filterName = (e.target as HTMLInputElement).value}
            />
          </div>
          
          <div class="filter-group">
            <span class="filter-label">Type:</span>
            <select 
              class="filter-select"
              .value=${this.filterType}
              @change=${(e: Event) => this.filterType = (e.target as HTMLSelectElement).value as any}
            >
              <option value="all">All</option>
              <option value="character">Characters</option>
              <option value="projectile">Projectiles</option>
              <option value="object">Objects</option>
            </select>
          </div>
          
          <div class="filter-group">
            <span class="filter-label">Visibility:</span>
            <select 
              class="filter-select"
              .value=${this.filterOnScreen}
              @change=${(e: Event) => this.filterOnScreen = (e.target as HTMLSelectElement).value as any}
            >
              <option value="all">All</option>
              <option value="onscreen">On Screen</option>
              <option value="offscreen">Off Screen</option>
            </select>
          </div>
          
          <div class="filter-group">
            <span class="filter-label" style="opacity: 0.6;">${filteredActors.length} / ${this.actors.length}</span>
          </div>
        </div>

        <div class="content-area">
          <div class="actor-list-section">
            <xstate-actor-list
              .actors=${filteredActors}
              .selectedId=${this.selectedActorId}
              .pinnedIds=${this.pinnedActorIds}
              @actor-selected=${this.handleActorSelected}
              @actor-pinned=${this.handleActorPinned}
            ></xstate-actor-list>
          </div>

          <div class="visualizer-section">
            ${this.selectedActorId ? html`
              <xstate-visualizer
                .interpreter=${this.getSelectedActor()}
                style="height: 100%; width: 100%; --visualizer-bg: transparent;"
              ></xstate-visualizer>
            ` : html`
              <div class="empty-state">Select an actor to view its state machine</div>
            `}
          </div>

          ${this.showEventLog && this.selectedActorId ? html`
            <div class="event-log-section">
              <div class="section-title">Event Log (${this.eventLog.length} events)</div>
              ${this.eventLog.length === 0 ? html`
                <div class="empty-state">No events recorded yet</div>
              ` : this.eventLog.slice().reverse().map(entry => this.renderEventEntry(entry))}
            </div>
          ` : ''}
        </div>
      </div>
    `;
  }

  private renderEventEntry(entry: EventLogEntry) {
    const time = new Date(entry.timestamp).toLocaleTimeString('en-US', { 
      hour12: false, 
      hour: '2-digit', 
      minute: '2-digit', 
      second: '2-digit',
      fractionalSecondDigits: 2
    });
    const transitioned = entry.transitioned;
    const stateChanged = entry.stateBefore !== entry.stateAfter;
    
    // Handle both old string format and new object format
    const eventObj = typeof entry.event === 'string' ? { type: entry.event } : entry.event;
    const eventType = eventObj.type;
    const animationName = eventObj.animationName;
    const eventDisplay = animationName ? `${eventType} (${animationName})` : eventType;
    
    return html`
      <div class="event-entry ${transitioned ? 'transitioned' : 'no-transition'}">
        <span class="event-time">${time}</span>
        <span class="event-type">${eventDisplay}</span>
        <span class="event-states">
          ${stateChanged ? html`
            <span class="state-before">${entry.stateBefore}</span>
            <span class="event-arrow">→</span>
            <span class="state-after">${entry.stateAfter}</span>
          ` : html`
            <span class="state-same">${entry.stateBefore} (no change)</span>
          `}
        </span>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'state-machines-panel': StateMachinesPanel;
  }
}
